/**
 * Floor plans — shared helpers.
 *
 * A floor plan is a market rent assumption: a layout name, its market rent,
 * and (optionally) its square feet. `square_feet` is null on models created
 * before floor plans carried size, so every consumer must tolerate it.
 *
 * These live outside the components because both the unit-by-unit grid
 * (UnitTable) and the unit-mix generator (UnitMixTable) create plans, and the
 * uniqueness rule below must behave identically in both.
 */

export interface MarketRentAssumption {
  layout: string;
  pf_rent: number;
  square_feet?: number | null;
}

/** Square feet a plan carries; 0 when it has none. */
export const planSquareFeet = (assumption?: MarketRentAssumption | null): number => {
  const sf = Number(assumption?.square_feet ?? 0);
  return !sf || isNaN(sf) || sf <= 0 ? 0 : sf;
};

/** Parse a typed square-footage into a positive whole number, or null. */
export const parseSqFt = (raw: string | number | null | undefined): number | null => {
  const cleaned = String(raw ?? '').replace(/,/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (isNaN(n) || n <= 0) return null;
  return Math.round(n);
};

/** Is this layout name already used by a plan? Case- and space-insensitive. */
export const layoutNameTaken = (
  assumptions: MarketRentAssumption[],
  name: string
): boolean => {
  const key = String(name ?? '').trim().toLowerCase();
  if (!key) return false;
  return assumptions.some((a) => a.layout.trim().toLowerCase() === key);
};

/**
 * Layout strings are INDEX/MATCH lookup keys in the generated sheet, so they
 * must stay unique per model. Rather than blocking a duplicate name outright,
 * offer a size-qualified variant — "1 Bedroom (820 SF)" — so two same-named
 * plans at different sizes are representable. Returns '' when there is no
 * size to disambiguate with.
 */
export const suggestLayoutName = (name: string, sqFt: number | null): string => {
  const trimmed = String(name ?? '').trim();
  if (!trimmed || !sqFt) return '';
  return `${trimmed} (${sqFt} SF)`;
};
